import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type MergeField } from 'entities/MergeField/model/MergeField'
import { ChatbotBookingType, ChatbotField } from 'entities/Chatbot'
import { IntegrationKey } from 'entities/Integrations'
import { mergeFieldReplacer, MessageFieldStore } from 'widgets/MessageField'
import { MergeFieldsIconAction } from 'widgets/MergeField/ui/actions/MergeFieldsIconAction/MergeFieldsIconAction'
import { StepObjectiveBookingStore } from 'pages/chatbot/ui/ChatbotEdit/StepObjective/store/StepObjectiveBookingStore'
import { type ChatbotStore } from 'pages/chatbot'

export class StepObjectiveStore {
  private readonly _messageFieldStore: MessageFieldStore
  private _disposeMessage: IReactionDisposer | null = null
  private _disposeError: IReactionDisposer | null = null
  private readonly _stepObjectiveBookingStore: StepObjectiveBookingStore

  constructor(private _chatbotStore: ChatbotStore, private _entities: IEntitiesStore) {
    this._messageFieldStore = new MessageFieldStore(
      {
        placeholder: (
          <>
            Example: Ask for{' '}
            <span className='mergeField-wrap disabled'>
              <span className='mergeField-content mergeField-salesmessage'>First Name</span>
            </span>{' '}
            and{' '}
            <span className='mergeField-wrap disabled'>
              <span className='mergeField-content mergeField-salesmessage'>Last Name</span>
            </span>
          </>
        ),
        noCounter: true,
        styles: {
          minHeight: '180px',
        },
        disableMergeFieldsFallback: true,
        makeActions: () => {
          return [
            {
              iconButtonComponent: (
                <MergeFieldsIconAction
                  onAddMergeField={(value) => this.onChooseMergeField(value)}
                  isAllIntegration
                  includeIntegrations={[IntegrationKey.hubspot]}
                  excludeFieldsIntegrations={{
                    [IntegrationKey.salesmessage]: ['number'],
                  }}
                />
              ),
            },
          ]
        },
      },
      this._entities
    )
    this._stepObjectiveBookingStore = new StepObjectiveBookingStore(this, this._entities)

    makeAutoObservable(this)
  }

  get chatbotStore() {
    return this._chatbotStore
  }

  get errors() {
    return this._chatbotStore.errors
  }

  get calendlyFieldsStore() {
    return this._chatbotStore.calendlyFieldsStore
  }

  get stepObjectiveBookingStore() {
    return this._stepObjectiveBookingStore
  }

  get chatbot() {
    return this._chatbotStore.model
  }

  get messageFieldStore() {
    return this._messageFieldStore
  }

  get connectCalendarStore() {
    return this._chatbotStore.agentBookAppointmentStepsStore.connectCalendarStore
  }

  get loading() {
    return this._stepObjectiveBookingStore.loading
  }

  get existMergeFields() {
    const bookingType = this.connectCalendarStore.bookingType

    return [...this._messageFieldStore.text.matchAll(/\{([^}]*)}/g)].reduce<
      Map<string, ChatbotField>
    >((state, [template, key]) => {
      const fieldData = this._entities.mergeFieldStore.getMergeFieldInfoByTemplate(template)

      key = key.trim()
      const field = fieldData.mergeField

      if (field) {
        if (bookingType === ChatbotBookingType.TimeChat) {
          state.set(
            key,
            new ChatbotField({
              key: key,
              name: field.label,
              type: field.typeCustom,
            })
          )
        }

        if (bookingType === ChatbotBookingType.ShareLink && !key.includes('calendly.')) {
          state.set(
            key,
            new ChatbotField({
              key: key,
              name: field.label,
              type: field.typeCustom,
            })
          )
        }
      }

      return state
    }, new Map())
  }

  validateBooking = () => {
    const isValidateRequiredFields = this._stepObjectiveBookingStore.validateRequiredFields()
    const isValidateMaxLimit = this.validateMaxLimit()
    const isValidateMinLimit = this.validateMinLimit(false)

    if (isValidateRequiredFields) return true
    if (isValidateMaxLimit) return true
    return isValidateMinLimit
  }

  validateMaxLimit = () => {
    this.errors.removeError('settings.data_collection_instruction.merge_fields_max_10')

    const messageFormFields = Array.from(this.existMergeFields.values())

    if (messageFormFields.length > 10) {
      this.errors.setError(
        'settings.data_collection_instruction.merge_fields_max_10',
        'You can select up to 10 fields. Please remove some selections to continue.'
      )

      return true
    }

    this.errors.removeError('settings.data_collection_instruction.merge_fields_max_10')

    return false
  }

  validateMinLimit = (disabledValidation = false) => {
    this.errors.removeError('settings.data_collection_instruction.merge_fields_min_1')

    if (disabledValidation) return false

    if (!this.existMergeFields.size) {
      this.errors.setError(
        'settings.data_collection_instruction.merge_fields_min_1',
        'Please add the merge fields the Agent should collect'
      )

      return true
    }

    this.errors.removeError('settings.data_collection_instruction.merge_fields_min_1')

    return false
  }

  onChooseMergeField = (field: MergeField) => {
    if (!this._messageFieldStore.addContent) return

    const fieldContent = mergeFieldReplacer(this._entities, field.template) + '&nbsp;'

    this._messageFieldStore.addContent(fieldContent)
  }

  initDefault = () => {
    this._initMessageField()
    this._reactionMessage()
    this._reactionError()
  }

  initBooking = () => {
    this._stepObjectiveBookingStore.init()
    this._reactionMessage()
    this._reactionError()
  }

  dispose = () => {
    this._disposeMessage?.()
    this._disposeError?.()
    this._messageFieldStore.reset()
    this._stepObjectiveBookingStore.dispose()
    this.errors.removeError('settings.data_collection_instruction.merge_fields_min_1')
    this.errors.removeError('settings.data_collection_instruction.merge_fields_max_10')
  }

  resetDataCollectionInstruction = () => {
    if (!this._chatbotStore.id) {
      this.chatbot.settings.setDataCollectionInstruction('')
    }
  }

  private _reactionMessage = () => {
    this._disposeMessage?.()
    this._disposeMessage = reaction(
      () => this._messageFieldStore.text,
      (value) => {
        this.chatbot.settings.setDataCollectionInstruction(value)
        this.resyncFields()
      }
    )
  }

  private get _allErrors() {
    return Boolean(
      this.errors.hasError('settings.data_collection_instruction.merge_fields_min_1') ||
        this.errors.hasError('settings.data_collection_instruction.merge_fields_max_10')
    )
  }

  private _reactionError = () => {
    this._disposeError?.()
    this._disposeError = reaction(
      () => [this._allErrors, this._stepObjectiveBookingStore.errorAlert],
      () => {
        const errorAlert = this._stepObjectiveBookingStore.errorAlert
        const _allErrors = this._allErrors
        if (_allErrors || errorAlert) {
          if (errorAlert) {
            if (errorAlert.type === 'alert' || errorAlert.type === 'error') {
              this._messageFieldStore.setHighlight('error')
            }
            if (errorAlert.type === 'warning') {
              this._messageFieldStore.setHighlight('warning')
            }
          } else {
            this._messageFieldStore.setHighlight('error')
          }
        } else {
          this._messageFieldStore.clearHighlight()
        }
      },
      {
        fireImmediately: true,
      }
    )
  }

  private _initMessageField = () => {
    const value = this.chatbot.settings.dataCollectionInstruction

    if (value) {
      this.setMessageField(value)
    }
  }

  setMessageField = (value: string) => {
    this._messageFieldStore.setMessageData({
      message: value,
      attachments: [],
      isReplace: true,
      replaceNewRow: false,
    })
    this.resyncFields()
  }

  resyncFields = () => {
    const fieldsMap = this.existMergeFields

    this.chatbot.fields.setItems(Array.from(fieldsMap.values()))
  }
}
